/**
 * Custom Product Section JavaScript
 * - Complete merged solution with edition and color filtering
 * - Handles variant changes, image updates, price updates
 * - Shows color swatches based on edition selection
 * - "Sold Out" when quantity = 0, "Unavailable" when variant doesn't exist
 * - FIXED: No "Unavailable" flash when switching editions
 */

class CustomProductSection {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId || null;
    
    // Get product data from inline JSON or window object
    this.productData = this._getProductData();
    this.updateImageTimeout = null;
    
    // Option positions
    this.editionPosition = null;
    this.colorPosition = null;
    
    // FIXED: Track if we're currently switching editions
    this.isSwitchingEdition = false;

    this.init();
  }

  init() {
    this.cacheDOMElements();
    this.detectOptionPositions();
    this.bindEvents();
    this.setupEditionColorFiltering();
    this.setupTestRideButton();
    
    // Initialize color visibility on page load
    setTimeout(() => {
      const initialEdition = this.getSelectedEdition();
      if (initialEdition) {
        console.log('━━━ Initial Edition:', initialEdition, '━━━');
        this.updateColorVisibility(initialEdition);
      }
    }, 100);
  }

  cacheDOMElements() {
    this.productImage = this.container.querySelector('[data-product-main-image]');
    this.priceContainer = this.container.querySelector('.custom-product-section__price');
    this.addToCartBtn = this.container.querySelector('.custom-product-section__btn--primary');
    this.form = this.container.querySelector('.custom-product-form');
    this.variantPicker = this.container.querySelector('variant-picker');
    this.testRideButton = this.container.querySelector('.test_ride_form_button a');
    this.editionInputs = this.container.querySelectorAll('[data-edition-switcher]');
    this.colorSwatches = this.container.querySelectorAll('.custom-color-swatch');
    this.colorDisplay = this.container.querySelector('[data-color-value]');
  }

  /**
   * Get product data from page
   */
  _getProductData() {
    try {
      // First try to get from form's product data
      const formId = this.container.querySelector('.custom-product-form')?.id;
      if (formId) {
        // Look for inline product JSON in the page
        const productScripts = document.querySelectorAll('script[type="application/json"]');
        for (let script of productScripts) {
          try {
            const data = JSON.parse(script.textContent);
            if (data.variants && Array.isArray(data.variants)) {
              return data;
            }
          } catch (e) {
            continue;
          }
        }
      }

      // Fallback: try window object
      if (window.productData) {
        return window.productData;
      }

      console.warn('Could not find product data');
      return null;
    } catch (e) {
      console.error('Error getting product data:', e);
      return null;
    }
  }

  /**
   * Detect which option is edition and which is color
   */
  detectOptionPositions() {
    if (!this.productData || !this.productData.options) {
      console.warn('No product data or options found');
      return;
    }

    this.productData.options.forEach((option, index) => {
      const optionLower = option.toLowerCase();
      const colorLabels = ['color', 'colour', 'couleur'];
      const editionLabels = ['edition', 'variant', 'version'];
      
      if (colorLabels.some(label => optionLower.includes(label))) {
        this.colorPosition = index + 1;
      } else if (editionLabels.some(label => optionLower.includes(label))) {
        this.editionPosition = index + 1;
      }
    });

    // Fallback: assume first is edition, second is color
    if (!this.editionPosition) this.editionPosition = 1;
    if (!this.colorPosition) this.colorPosition = 2;
  }

  /**
   * Setup edition and color filtering
   */
  setupEditionColorFiltering() {
    if (!this.editionInputs.length) {
      console.log('No edition inputs found');
      return;
    }

    // Handle edition change
    this.editionInputs.forEach(input => {
      input.addEventListener('change', () => {
        if (input.checked) {
          // FIXED: Set flag to prevent "Unavailable" flash
          this.isSwitchingEdition = true;
          
          const selectedEdition = this.getEditionValue(input);
          console.log('━━━ Edition changed to:', selectedEdition, '━━━');
          
          // Update visual state of edition buttons
          this.editionInputs.forEach(inp => {
            inp.closest('.custom-block-option')?.classList.remove('is-selected');
          });
          input.closest('.custom-block-option')?.classList.add('is-selected');
          
          // Update color visibility and auto-select first color
          this.updateColorVisibilityAndSelect(selectedEdition);
        }
      });
    });

    // Handle color swatch visual updates
    const colorInputs = this.container.querySelectorAll('.custom-color-swatch input[type="radio"]');
    colorInputs.forEach(input => {
      input.addEventListener('change', () => {
        this.handleColorSwatchVisualUpdate(input);
      });
    });
  }

  /**
   * Get edition value from input
   */
  getEditionValue(input) {
    const label = input.closest('label');
    if (label) {
      const span = label.querySelector('span');
      if (span) return span.textContent.trim();
    }
    return input.value;
  }

  /**
   * Get currently selected edition
   */
  getSelectedEdition() {
    const checkedEdition = this.container.querySelector('[data-edition-switcher]:checked');
    if (checkedEdition) {
      return this.getEditionValue(checkedEdition);
    }
    return null;
  }

  /**
   * FIXED: Update color visibility AND auto-select in one flow
   */
  updateColorVisibilityAndSelect(selectedEdition) {
    if (!this.productData || !this.productData.variants) {
      console.warn('No product data available for filtering');
      this.isSwitchingEdition = false;
      return;
    }
     
    let visibleColorFound = false;
    let firstVisibleSwatch = null;

    this.colorSwatches.forEach(swatch => {
      const colorName = swatch.dataset.colorOption;
      
      // Check if variant EXISTS for this edition + color (ignore stock/availability)
      let variantExists = false;
      
      this.productData.variants.forEach(variant => {
        const variantEdition = variant['option' + this.editionPosition];
        const variantColor = variant['option' + this.colorPosition];
        
        // Match by edition and color
        if (
          variantEdition === selectedEdition &&
          variantColor === colorName &&
          !variant.hide_variant
        ) {
          variantExists = true;
        }
      });
      
      // Show/hide based on existence (NOT availability)
      if (variantExists) {
        swatch.style.display = 'flex';
        visibleColorFound = true;
        if (!firstVisibleSwatch) {
          firstVisibleSwatch = swatch;
        }
      } else {
        swatch.style.display = 'none';
      }
    });

    // Auto-select first visible color if current selection is hidden
    const currentSelectedSwatch = this.container.querySelector('.custom-color-swatch.is-selected');
    const currentSelectedVisible = currentSelectedSwatch && currentSelectedSwatch.style.display !== 'none';

    if (!currentSelectedVisible && firstVisibleSwatch) {
      const input = firstVisibleSwatch.querySelector('input[type="radio"]');
      if (input) {
        console.log('→ Auto-selecting first visible color');
        
        // Remove selection from all
        this.colorSwatches.forEach(s => s.classList.remove('is-selected'));
        
        // Select first visible
        input.checked = true;
        firstVisibleSwatch.classList.add('is-selected');
        
        // FIXED: Manually trigger variant change immediately (no delay)
        const variant = this._findCurrentVariant();
        if (variant) {
          this._syncHiddenIdInput(variant);
          this.handleVariantChange(variant);
          this.broadcastVariantChange(variant);
        }
        
        // Reset flag after variant is found
        setTimeout(() => {
          this.isSwitchingEdition = false;
        }, 100);
      }
    } else {
      // No auto-selection needed
      this.isSwitchingEdition = false;
    }
  }

  /**
   * DEPRECATED: Replaced by updateColorVisibilityAndSelect
   */
  updateColorVisibility(selectedEdition) {
    this.updateColorVisibilityAndSelect(selectedEdition);
  }

  bindEvents() {
    if (!this.form) return;

    // Impact theme variant:change event
    this.form.addEventListener('variant:change', (e) => {
      if (e.detail && e.detail.variant) {
        this.handleVariantChange(e.detail.variant);
        this.broadcastVariantChange(e.detail.variant);
      } else {
        // FIXED: Don't show "Unavailable" if we're switching editions
        if (!this.isSwitchingEdition) {
          this.handleInvalidVariant();
        }
      }
    });

    // Fallback for custom radios
    const optionInputs = this.container.querySelectorAll(
      'input[data-option-position][name^="' + this.form.id + '-option"]'
    );

    optionInputs.forEach((input) => {
      input.addEventListener('change', () => {
        const variant = this._findCurrentVariant();
        if (variant) {
          this._syncHiddenIdInput(variant);
          this.handleVariantChange(variant);
          this.broadcastVariantChange(variant);
        } else {
          console.warn('No variant found for selected options');
          // FIXED: Don't show "Unavailable" if we're switching editions
          if (!this.isSwitchingEdition) {
            this.handleInvalidVariant();
          }
        }
      });
    });
  }

  /**
   * Handle invalid variant (doesn't exist)
   */
  handleInvalidVariant() {
    if (this.addToCartBtn) {
      this.addToCartBtn.disabled = true;
      this.addToCartBtn.classList.add('is-disabled');
      const btnText = this.addToCartBtn.querySelector('span');
      if (btnText) {
        btnText.textContent = 'Unavailable';
      }
    }
  }

  /**
   * Setup test ride button
   */
  setupTestRideButton() {
    if (!this.testRideButton) return;

    this.testRideButton.addEventListener('click', () => {
      const productHandle = this.variantPicker ? 
        this.variantPicker.getAttribute('handle') : 
        this._getProductHandleFromURL();
      const variantId = this._getCurrentVariantId();
      const productTitle = this.container.querySelector('.custom-product-section__title')?.textContent.trim();
      const variantTitle = this._getCurrentVariantTitle();

      if (productHandle && variantId) {
        const testRideData = {
          productHandle,
          variantId,
          productTitle: productTitle || '',
          variantTitle: variantTitle || '',
          timestamp: Date.now()
        };

        try {
          sessionStorage.setItem('bookTestRideData', JSON.stringify(testRideData));
          console.log('✓ Stored test ride data');
        } catch (error) {
          console.error('Failed to store test ride data:', error);
        }
      }
    });
  }

  _getCurrentVariantId() {
    if (!this.form) return null;
    const idInput = this.form.querySelector('input[name="id"]');
    return idInput?.value || null;
  }

  _getCurrentVariantTitle() {
    const optionInputs = this.container.querySelectorAll('input[data-option-position]:checked');
    
    const options = Array.from(optionInputs)
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map(input => {
        const label = input.closest('label');
        const span = label?.querySelector('span');
        return span ? span.textContent.trim() : input.value;
      });

    return options.join(' / ');
  }

  _getProductHandleFromURL() {
    const match = window.location.pathname.match(/\/products\/([^?/]+)/);
    return match ? match[1] : null;
  }

  /**
   * Broadcast variant change
   */
  broadcastVariantChange(variant) {
    if (!variant) return;

    document.dispatchEvent(new CustomEvent('variant:change', {
      detail: { variant },
      bubbles: true,
      composed: true
    }));
  }

  /**
   * Update color display text
   */
  updateColorDisplay(variant) {
    if (!this.colorDisplay || !variant) return;
    const colorName = variant['option' + this.colorPosition];
    if (colorName) {
      this.colorDisplay.textContent = colorName;
    }
  }

  /**
   * Handle color swatch visual update
   */
  handleColorSwatchVisualUpdate(input) {
    this.colorSwatches.forEach(swatch => swatch.classList.remove('is-selected'));
    input.closest('.custom-color-swatch')?.classList.add('is-selected');
  }

  /**
   * Handle variant change
   */
  handleVariantChange(variant) {
    if (!variant) {
      // FIXED: Don't show "Unavailable" if we're switching editions
      if (!this.isSwitchingEdition) {
        this.handleInvalidVariant();
      }
      return;
    }

    this.updateProductImage(variant);
    this.updatePrice(variant);
    this.updateButtonState(variant);
    this.updateColorDisplay(variant);
  }

  /**
   * Update product image
   */
  updateProductImage(variant) {
    if (!this.productImage || !variant.featured_media?.preview_image) return;

    const newSrc = variant.featured_media.preview_image.src;
    if (this.productImage.src.includes(newSrc)) return;

    if (this.updateImageTimeout) clearTimeout(this.updateImageTimeout);

    this.productImage.style.transition = 'opacity 0.25s ease';
    this.productImage.style.opacity = '0.4';

    this.updateImageTimeout = setTimeout(() => {
      this.productImage.onload = () => {
        this.productImage.style.opacity = '1';
      };
      this.productImage.src = newSrc;
      this.productImage.alt = variant.featured_media.alt || variant.name || '';
    }, 120);
  }

  /**
   * Update price via AJAX
   */
  updatePrice(variant) {
    return;
  }

  /**
   * Update button state
   */
  updateButtonState(variant) {
    if (!this.addToCartBtn) return;

    const btnText = this.addToCartBtn.querySelector('span');
    if (!btnText) return;

    const bookOnlineText = this.container.closest('[id^="shopify-section-"]')
      ?.querySelector('[data-book-online-text]')?.dataset.bookOnlineText || 'Book Online';

    if (!variant) {
      // No variant exists - FIXED: Only show if not switching editions
      if (!this.isSwitchingEdition) {
        this.addToCartBtn.disabled = true;
        this.addToCartBtn.classList.add('is-disabled');
        btnText.textContent = 'Unavailable';
      }
      return;
    }

    if (variant.available) {
      // In stock
      this.addToCartBtn.disabled = false;
      this.addToCartBtn.classList.remove('is-disabled');
      btnText.textContent = bookOnlineText;
    } else {
      // Out of stock (quantity = 0)
      this.addToCartBtn.disabled = true;
      this.addToCartBtn.classList.add('is-disabled');
      btnText.textContent = 'Sold Out';
    }

    this._syncHiddenIdInput(variant);
  }

  _syncHiddenIdInput(variant) {
    if (!this.form) return;
    const idInput = this.form.querySelector('input[name="id"]');
    if (idInput && String(idInput.value) !== String(variant.id)) {
      idInput.value = variant.id;
    }
  }

  /**
   * Find current variant based on selected options
   */
  _findCurrentVariant() {
    if (!this.form || !this.productData?.variants) return null;

    const optionInputs = this.container.querySelectorAll(
      'input[data-option-position][name^="' + this.form.id + '-option"]:checked'
    );

    if (!optionInputs.length) return null;

    // Build selected options map
    const selectedOptions = {};
    optionInputs.forEach(input => {
      const position = Number(input.dataset.optionPosition);
      const label = input.closest('label');
      const span = label?.querySelector('span');
      selectedOptions[position] = span ? span.textContent.trim() : input.value;
    });

    console.log('Finding variant for:', selectedOptions);

    // Find matching variant
    const match = this.productData.variants.find(variant => {
      return (
        !variant.hide_variant &&
        Object.keys(selectedOptions).every(pos => {
          const key = 'option' + pos;
          return String(variant[key]) === String(selectedOptions[pos]);
        })
      );
    });

    if (match) {
      console.log('Found variant:', match.id, match.title);
    } else {
      console.log('No variant found');
    }

    return match || null;
  }
}

/**
 * Custom Add Button
 */
class CustomAddButton extends HTMLButtonElement {
  constructor() {
    super();
    this.addEventListener('click', this.onButtonClick.bind(this));
  }

  onButtonClick(event) {
    event.preventDefault();
    const formId = this.getAttribute('form');
    const form = (formId && document.getElementById(formId)) || this.closest('form');
    if (form instanceof HTMLFormElement) {
      form.requestSubmit();
    }
  }
}

if (!window.customElements.get('custom-add-button')) {
  window.customElements.define('custom-add-button', CustomAddButton, { extends: 'button' });
}

/* Initialization */
function initCustomProductSections() {
  const sections = document.querySelectorAll('.custom-product-section');
  sections.forEach((section) => {
    try {
      new CustomProductSection(section);
      console.log('✓ Custom product section initialized');
    } catch (error) {
      console.error('Failed to initialize:', error);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCustomProductSections);
} else {
  initCustomProductSections();
}

// Shopify theme editor
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('.custom-product-section');
    if (section) {
      try {
        new CustomProductSection(section);
      } catch (error) {
        console.error('Failed to initialize in editor:', error);
      }
    }
  });
}